import { useMemo, useState } from 'react'
import { Download, FileText, Search, Building2, ShieldCheck, ClipboardList } from 'lucide-react'
import { usePublicNoticeOverview } from '@/hooks/useData'

const emptyArray: never[] = []

export default function Disclosure() {
  const { data, isLoading } = usePublicNoticeOverview()
  const [activeCategory, setActiveCategory] = useState<string>('all')
  const [keyword, setKeyword] = useState('')

  const categories = useMemo(() => {
    if (!data) return emptyArray
    return data.categories
  }, [data])

  const filteredItems = useMemo(() => {
    if (!data) return emptyArray
    return data.items.filter((item) => {
      const matchCategory =
        activeCategory === 'all' || item.category === activeCategory || item.category === categories.find((c) => c.id === activeCategory)?.name
      const matchKeyword =
        keyword.trim().length === 0 ||
        [item.title, item.department, item.summary, item.reference].some((field) => field && field.toLowerCase().includes(keyword.toLowerCase()))
      return matchCategory && matchKeyword
    })
  }, [data, activeCategory, keyword, categories])

  return (
    <div className="min-h-screen bg-white">
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-10 items-start">
            <div>
              <p className="text-sm uppercase tracking-[0.35em] text-primary-500">INFORMATION DISCLOSURE</p>
              <h1 className="mt-3 text-3xl lg:text-4xl font-extrabold text-secondary-900">信息公示专栏</h1>
              <p className="mt-4 text-secondary-600 text-lg leading-relaxed">
                依据《中华人民共和国政府信息公开条例》等相关法规，及时向社会发布公司资质、项目执行、监督检查等权威信息，接受公众监督，保障服务公开透明。
              </p>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-primary-100 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                      <p className="text-xs text-secondary-500">累计公示</p>
                      <p className="text-xl font-bold text-primary-700">{data?.statistics.total ?? '—'} 条</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-primary-100 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                      <p className="text-xs text-secondary-500">年度新增</p>
                      <p className="text-xl font-bold text-primary-700">{data?.statistics.yearly ?? '—'} 条</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-primary-100 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                      <ShieldCheck className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                      <p className="text-xs text-secondary-500">最新更新</p>
                      <p className="text-xl font-bold text-primary-700">{data?.statistics.updated ?? '—'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white border border-primary-100 rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-secondary-900 flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-primary-600" />
                监督渠道
              </h2>
              <p className="text-sm text-secondary-600 mt-3 leading-relaxed">
                欢迎社会公众及合作单位对我司环保检测活动进行监督。若发现不符合规定的行为，可通过以下方式反馈：
              </p>
              <ul className="mt-4 space-y-3 text-sm text-secondary-600">
                <li>监督电话：0371-67116867（工作日 9:00-18:00）</li>
                <li>监督邮箱：hnaedhb@163.com</li>
                <li>通信地址：郑州市二七区马寨镇工业路11号院6号楼2楼</li>
              </ul>
              <p className="mt-4 text-xs text-secondary-400">我们将严格保护举报人信息，并在7个工作日内给出处理反馈。</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-secondary-200 rounded-2xl shadow-sm">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between p-6 border-b border-secondary-200">
              <div className="flex flex-wrap gap-2">
                <button
                  className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${
                    activeCategory === 'all'
                      ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                      : 'border-secondary-200 text-secondary-600 hover:border-primary-400 hover:text-primary-600'
                  }`}
                  onClick={() => setActiveCategory('all')}
                >
                  全部信息
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${
                      activeCategory === category.id
                        ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                        : 'border-secondary-200 text-secondary-600 hover:border-primary-400 hover:text-primary-600'
                    }`}
                    onClick={() => setActiveCategory(category.id)}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
              <div className="relative w-full md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  placeholder="输入关键词检索"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="w-full rounded-full border border-secondary-200 bg-secondary-50 py-2 pl-10 pr-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-50/70">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-500 uppercase tracking-wider">公示标题</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-500 uppercase tracking-wider hidden lg:table-cell">发布单位</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-500 uppercase tracking-wider">发布时间</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-500 uppercase tracking-wider hidden md:table-cell">状态</th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-100">
                  {isLoading && (
                    <tr>
                      <td className="px-6 py-8 text-center text-secondary-500" colSpan={5}>
                        正在加载信息...
                      </td>
                    </tr>
                  )}
                  {!isLoading && filteredItems.length === 0 && (
                    <tr>
                      <td className="px-6 py-12 text-center text-secondary-500" colSpan={5}>
                        暂无符合条件的公示信息。
                      </td>
                    </tr>
                  )}
                  {filteredItems.map((item) => (
                    <tr key={item.id} className="hover:bg-secondary-50/60 transition-colors">
                      <td className="px-6 py-4">
                        <p className="text-sm font-semibold text-secondary-900 line-clamp-2">{item.title}</p>
                        <p className="text-xs text-secondary-500 mt-1 line-clamp-1">{item.summary}</p>
                        {item.reference && (
                          <p className="text-xs text-primary-500 mt-1">文号：{item.reference}</p>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-secondary-600 hidden lg:table-cell">{item.department}</td>
                      <td className="px-6 py-4 text-sm text-secondary-600">{item.publishDate}</td>
                      <td className="px-6 py-4 text-sm text-primary-600 hidden md:table-cell">{item.status ?? '—'}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {item.attachments && item.attachments.length > 0 ? (
                            item.attachments.map((attachment, index) => (
                              <a
                                key={`${item.id}-att-${index}`}
                                href={attachment.url}
                                className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50"
                              >
                                <Download className="h-4 w-4" />
                                {attachment.name}
                              </a>
                            ))
                          ) : (
                            <span className="text-xs text-secondary-400">无需附件</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
